"use client";

import { useState, useEffect, useMemo, ReactNode } from "react";
import IntlLink from "../(utils)/IntlLink";
import { useTranslation } from "../../i18n/client";
import { SvgArrowLeft } from "../(svg)/SvgAll";

interface NavigationItem {
  id: string;
  title: string;
}

interface DocumentLayoutProps {
  params: { lng: string };
  titleKey: string;
  navigationItems: NavigationItem[];
  children: ReactNode;
  basePath: string;
}

const DocumentLayout = ({ 
  params, 
  titleKey, 
  navigationItems, 
  children, 
  basePath 
}: DocumentLayoutProps) => {
  const { lng } = params;
  const { t }: { t: any } = useTranslation(lng, "new-landing");
  const [activeSection, setActiveSection] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const memoizedNavigationItems = useMemo(() => navigationItems, [navigationItems]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = memoizedNavigationItems
        .map((item) => document.getElementById(item.id))
        .filter(Boolean);

      if (sections.length === 0) return;

      const scrollPosition = window.scrollY + 200;
      let foundSection = "";

      for (let i = sections.length - 1; i >= 0; i -= 1) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          foundSection = section.id;
          break;
        }
      }

      if (foundSection !== activeSection) {
        setActiveSection(foundSection);
        const newUrl = foundSection
          ? `/${lng}${basePath}#${foundSection}`
          : `/${lng}${basePath}`;
        window.history.replaceState(null, "", newUrl);
      }
    };

    const throttledScroll = () => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(handleScroll, 100);
      };
    };

    const scrollHandler = throttledScroll();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [memoizedNavigationItems, activeSection, lng, basePath]);

  return (
    <div className="bg-white px-4 sm:px-8 pb-16 font-montserrat">
      <div className="relative flex flex-row bg-white container mx-auto py-7">
        <IntlLink
          url="/"
          type="button"
          className="flex items-center gap-3 text-[#031F47] font-bold text-[18px] hover:opacity-70 transition-opacity duration-200"
        >
          <SvgArrowLeft />
          {t("go_back_form")}
        </IntlLink>
      </div>
      <div className="container mx-auto">
        <h1 className="font-bold text-[30px] sm:text-[48px] text-[#031F47] mb-12 lg:mb-[100px] text-center">
          {t(`documents.${titleKey}`)}
        </h1>
      </div>

      <div className="lg:hidden container mx-auto mb-8 relative">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
        >
          <span className="font-semibold text-[#031F47]">
            {activeSection 
              ? memoizedNavigationItems.find(item => item.id === activeSection)?.title 
              : t("documents.table-of-contents")
            }
          </span>
          <svg 
            className={`w-5 h-5 transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto scrollbar-hide z-50">
            {memoizedNavigationItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-sm font-bold text-[#031F47] transition-colors duration-200 ${
                    isActive
                      ? "text-primary bg-blue-50 font-semibold"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {item.title}
                </a>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex container mx-auto flex-1 gap-12">
        <div className="hidden lg:block sticky top-4 h-fit max-w-[480px] w-full">
          <div>
            <div className="font-bold text-[24px] leading-[140%] mb-8 text-[#031F47]">
              {t("documents.table-of-contents")}
            </div>
            <div className="max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-hide">
              <div className="flex flex-col gap-4">
                {memoizedNavigationItems.map((item) => {
                  const isActive = activeSection === item.id;
                  return (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      className={`group relative flex items-center transition-colors duration-200 font-semibold text-[20px] line-height-[140%] ${
                        isActive
                          ? "text-[#3484F7]"
                          : "text-neutral-1000 hover:text-[#3484F7]"
                      }`}
                    >
                      <div
                        className={`absolute left-0 w-1 h-[30px] transition-opacity duration-200 bg-[#3484F7] ${
                          isActive
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                      />
                      <span className="ml-2">{item.title}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        <div className="w-full lg:w-3/5 flex flex-col gap-12 text-neutral-1000">
          {children}
        </div>
      </div>
    </div>
  );
};

export default DocumentLayout;
